/**
 * Parse Client Configuration
 * 
 * เชื่อมต่อกับ Parse Server ผ่าน REST API (ไม่ใช้ SDK)
 * 
 * ⚠️ Config ทั้งหมดอยู่ที่ @/config/app.config.ts
 */

import { 
  PARSE_SERVER_URL, 
  PARSE_APP_ID, 
  PARSE_MASTER_KEY, 
  PARSE_JS_KEY,
  PARSE_V2_SERVER_URL,
  PARSE_V2_APP_ID,
  PARSE_V2_MASTER_KEY,
  DEBUG_MODE 
} from '@/config/app.config';
import { getBackendType } from '@/config/api.config';

// Ensure URL doesn't have trailing slash
const normalizeUrl = (url: string) => url.replace(/\/+$/, '');

// Get current backend config dynamically
export function getCurrentParseConfig() {
  const backendType = getBackendType();
  
  if (backendType === 'parse-v2') {
    return {
      serverURL: normalizeUrl(PARSE_V2_SERVER_URL),
      altServerURL: normalizeUrl(PARSE_V2_SERVER_URL).replace('/api/v2', '/api/parse'),
      appId: PARSE_V2_APP_ID,
      jsKey: '',
      masterKey: PARSE_V2_MASTER_KEY,
    };
  }
  
  // Fallback to V1 (รองรับการสลับกลับไปใช้ v1 ได้)
  return {
    serverURL: normalizeUrl(PARSE_SERVER_URL),
    altServerURL: normalizeUrl(PARSE_SERVER_URL).replace('/api/v1', '/api/parse'),
    appId: PARSE_APP_ID,
    jsKey: PARSE_JS_KEY,
    masterKey: PARSE_MASTER_KEY,
  };
}

// Export for backward compatibility (uses current backend)
export const PARSE_CONFIG = getCurrentParseConfig();

// Log config on load (for debugging)
if (DEBUG_MODE) {
  const config = getCurrentParseConfig();
  console.log('🔧 Parse Config:', {
    backend: getBackendType(),
    serverURL: config.serverURL,
    altURL: config.altServerURL,
    appId: config.appId ? '***' + config.appId.slice(-8) : 'NOT SET',
  });
}

// Common headers for Parse REST API
function getHeaders(useMasterKey: boolean = true): HeadersInit {
  const config = getCurrentParseConfig();
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    'X-Parse-Application-Id': config.appId,
  };
  
  if (useMasterKey && config.masterKey) {
    headers['X-Parse-Master-Key'] = config.masterKey;
  } else if (config.jsKey) {
    headers['X-Parse-JavaScript-Key'] = config.jsKey;
  }
  
  return headers;
}

// Test connection - try both /api/v1 and /parse endpoints
export async function testConnection(): Promise<{ success: boolean; message: string; serverInfo?: any; workingUrl?: string }> {
  const config = getCurrentParseConfig();
  // Try both base URLs
  const baseUrls = [config.serverURL, config.altServerURL];
  
  // Endpoints to try for each base URL
  const endpointPaths = [
    { path: '/schemas', name: 'schemas' },
    { path: '/classes/_User?limit=1', name: '_User class' },
    { path: '/health', name: 'health' },
  ];
  
  console.log('🔌 Testing Parse Server connection...');
  console.log('📋 Primary URL:', config.serverURL);
  console.log('📋 Alt URL:', config.altServerURL);
  
  for (const baseUrl of baseUrls) {
    console.log(`\n🔍 Trying base: ${baseUrl}`);
    
    for (const endpoint of endpointPaths) {
      const fullUrl = `${baseUrl}${endpoint.path}`;
      try {
        console.log(`   → ${fullUrl}`);
        const response = await fetch(fullUrl, {
          headers: getHeaders(),
        });
        
        console.log(`     Response: ${response.status} ${response.statusText}`);
        
        if (response.ok) {
          const data = await response.json().catch(() => ({}));
          console.log('✅ Success:', endpoint.name);
          
          return {
            success: true,
            message: `Connected via ${endpoint.name} (${baseUrl})`,
            serverInfo: data,
            workingUrl: baseUrl,
          };
        }
        
        // Log error response for debugging
        if (response.status === 401 || response.status === 403) {
          const errorData = await response.json().catch(() => ({}));
          console.log('🔐 Auth error:', errorData);
          // Auth error means server is reachable, just credentials issue
          return {
            success: false,
            message: `Auth failed (${response.status}): Check App ID / Master Key`,
            serverInfo: errorData,
          };
        }
      } catch (e) {
        console.log(`     ❌ Error:`, (e as Error).message);
      }
    }
  }
  
  return {
    success: false,
    message: `Connection failed. Tried: ${baseUrls.join(', ')}`,
  };
}

// Get all schemas (collections)
export async function getAllSchemas(): Promise<any[]> {
  try {
    const config = getCurrentParseConfig();
    const response = await fetch(`${config.serverURL}/schemas`, {
      headers: getHeaders(),
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Failed to get schemas: ${response.statusText}`);
    }
    
    const data = await response.json();
    return data.results || [];
  } catch (error) {
    console.error('Error getting schemas:', error);
    throw error;
  }
}

// Get schema for specific class
export async function getClassSchema(className: string): Promise<any> {
  try {
    const config = getCurrentParseConfig();
    const response = await fetch(`${config.serverURL}/schemas/${className}`, {
      headers: getHeaders(),
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Failed to get schema for ${className}: ${response.statusText}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error(`Error getting schema for ${className}:`, error);
    throw error;
  }
}

// Safe JSON parse helper (moved here for use by all functions)
async function safeParseJson(response: Response): Promise<any> {
  const text = await response.text();
  if (!text || text.trim() === '') {
    console.warn('⚠️ Empty response body');
    return {};
  }
  try {
    return JSON.parse(text);
  } catch (e) {
    console.error('❌ JSON parse error:', text.substring(0, 200));
    throw new Error(`Invalid JSON response: ${text.substring(0, 100)}`);
  }
}

// Query class data (with optional session token)
export async function queryClass(
  className: string,
  options: {
    limit?: number;
    skip?: number;
    where?: Record<string, any>;
    order?: string;
    include?: string[];
    keys?: string[];
    count?: number;
  } = {},
  sessionToken?: string
): Promise<{ results: any[]; count: number }> {
  try {
    const params = new URLSearchParams();
    
    if (options.limit !== undefined) params.set('limit', String(options.limit));
    if (options.skip) params.set('skip', String(options.skip));
    if (options.where) params.set('where', JSON.stringify(options.where));
    if (options.order) params.set('order', options.order);
    if (options.include) params.set('include', options.include.join(','));
    if (options.keys) params.set('keys', options.keys.join(','));
    if (options.count !== undefined) params.set('count', String(options.count));
    else params.set('count', '1');
    
    const config = getCurrentParseConfig();
    // Build headers with session token if provided
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      'X-Parse-Application-Id': config.appId,
    };
    
    if (sessionToken) {
      headers['X-Parse-Session-Token'] = sessionToken;
    }
    // Also add master key for broader access
    if (config.masterKey) {
      headers['X-Parse-Master-Key'] = config.masterKey;
    }
    
    console.log(`📊 [Query] ${className}`, { where: options.where, limit: options.limit });
    
    const response = await fetch(`${config.serverURL}/classes/${className}?${params}`, {
      headers,
    });
    
    if (!response.ok) {
      const errorData = await safeParseJson(response);
      console.error(`❌ [Query] ${className} failed:`, errorData);
      throw new Error(errorData.error || `Failed to query ${className}: ${response.statusText}`);
    }
    
    const result = await safeParseJson(response);
    console.log(`✅ [Query] ${className}: ${result.results?.length || 0} results, total: ${result.count || 0}`);
    return { results: result.results || [], count: result.count || 0 };
  } catch (error) {
    console.error(`Error querying ${className}:`, error);
    throw error;
  }
}

// Get single object by ID
export async function getObjectById(className: string, objectId: string): Promise<any> {
  try {
    const config = getCurrentParseConfig();
    const response = await fetch(`${config.serverURL}/classes/${className}/${objectId}`, {
      headers: getHeaders(),
    });
    
    if (!response.ok) {
      const errorData = await safeParseJson(response);
      throw new Error(errorData.error || `Failed to get ${className}/${objectId}: ${response.statusText}`);
    }
    
    return await safeParseJson(response);
  } catch (error) {
    console.error(`Error getting ${className}/${objectId}:`, error);
    throw error;
  }
}

// Create object
export async function createObject(className: string, data: Record<string, any>): Promise<any> {
  try {
    const config = getCurrentParseConfig();
    console.log(`📤 [Parse] Creating ${className}:`, data);
    
    const response = await fetch(`${config.serverURL}/classes/${className}`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    
    console.log(`📥 [Parse] Response: ${response.status} ${response.statusText}`);
    
    if (!response.ok) {
      const errorData = await safeParseJson(response);
      throw new Error(errorData.error || `Failed to create ${className}: ${response.statusText}`);
    }
    
    const result = await safeParseJson(response);
    console.log(`✅ [Parse] Created ${className}:`, result);
    return result;
  } catch (error) {
    console.error(`❌ Error creating ${className}:`, error);
    throw error;
  }
}

// Update object
export async function updateObject(className: string, objectId: string, data: Record<string, any>): Promise<any> {
  try {
    const config = getCurrentParseConfig();
    console.log(`📤 [Parse] Updating ${className}/${objectId}:`, data);
    
    const response = await fetch(`${config.serverURL}/classes/${className}/${objectId}`, {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    
    console.log(`📥 [Parse] Response: ${response.status} ${response.statusText}`);
    
    if (!response.ok) {
      const errorData = await safeParseJson(response);
      throw new Error(errorData.error || `Failed to update ${className}/${objectId}: ${response.statusText}`);
    }
    
    const result = await safeParseJson(response);
    console.log(`✅ [Parse] Updated ${className}/${objectId}:`, result);
    return result;
  } catch (error) {
    console.error(`❌ Error updating ${className}/${objectId}:`, error);
    throw error;
  }
}

// Delete object
export async function deleteObject(className: string, objectId: string): Promise<void> {
  try {
    const config = getCurrentParseConfig();
    const response = await fetch(`${config.serverURL}/classes/${className}/${objectId}`, {
      method: 'DELETE',
      headers: getHeaders(),
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Failed to delete ${className}/${objectId}: ${response.statusText}`);
    }
  } catch (error) {
    console.error(`Error deleting ${className}/${objectId}:`, error);
    throw error;
  }
}

// Call Cloud Function
export async function callCloudFunction(functionName: string, params: Record<string, any> = {}, sessionToken?: string): Promise<any> {
  try {
    // Get current backend config (supports V1/V2 switching)
    const config = getCurrentParseConfig();
    const backendType = getBackendType();
    
    console.log(`📤 [Parse] Calling function ${functionName} (${backendType}):`, params);
    
    // Build headers with session token if provided
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      'X-Parse-Application-Id': config.appId,
    };
    
    if (sessionToken) {
      headers['X-Parse-Session-Token'] = sessionToken;
      // ✅ ไม่ส่ง Master Key เมื่อมี Session Token เพื่อให้ req.user ถูก set
    } else if (config.masterKey) {
      // ส่ง Master Key เฉพาะเมื่อไม่มี Session Token
      headers['X-Parse-Master-Key'] = config.masterKey;
    }
    
    // ✅ Add device headers if available (non-blocking)
    try {
      const { fingerprintService } = await import('@/services/fingerprint.service');
      const deviceHeaders = await fingerprintService.getDeviceHeaders();
      Object.assign(headers, deviceHeaders);
    } catch (deviceError) {
      // Non-critical - continue without device headers
    }
    
    const url = `${config.serverURL}/functions/${functionName}`;
    console.log(`🌐 [Parse] Request URL: ${url}`);
    console.log(`🌐 [Parse] Backend Type: ${backendType}`);
    console.log(`🌐 [Parse] Config:`, {
      serverURL: config.serverURL,
      appId: config.appId ? '***' + config.appId.slice(-8) : 'NOT SET',
    });
    
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(params),
    });
    
    console.log(`📥 [Parse] Response: ${response.status} ${response.statusText}`);
    console.log(`📥 [Parse] Response URL: ${response.url}`);
    
    if (!response.ok) {
      const errorData = await safeParseJson(response);
      
      // ✅ Handle invalid session token (code 209)
      if (errorData.code === 209 || errorData.error?.includes('Invalid session token')) {
        console.warn(`⚠️ [Parse] Invalid session token for function ${functionName}`);
        // ✅ Dispatch global event → App.tsx จะ logout + redirect /login
        window.dispatchEvent(new CustomEvent('session-expired'));
        const sessionError = new Error('SESSION_EXPIRED');
        (sessionError as any).code = 209;
        throw sessionError;
      }
      
      throw new Error(errorData.error || `Failed to call ${functionName}: ${response.statusText}`);
    }
    
    const data = await safeParseJson(response);
    console.log(`✅ [Parse] Function ${functionName} result:`, data.result);
    return data.result;
  } catch (error) {
    console.error(`❌ Error calling ${functionName}:`, error);
    throw error;
  }
}

// Get server config
export async function getServerConfig(): Promise<any> {
  try {
    const config = getCurrentParseConfig();
    const response = await fetch(`${config.serverURL}/config`, {
      headers: getHeaders(),
    });
    
    if (!response.ok) {
      return null;
    }
    
    const data = await response.json();
    return data.params || {};
  } catch (error) {
    console.error('Error getting config:', error);
    return null;
  }
}

// ============================================
// Authentication Functions
// ============================================

export interface LoginResult {
  success: boolean;
  user?: {
    objectId: string;
    sessionToken: string;
    username: string;
    email?: string;
    phone?: string;
    role?: string;
    isAdmin?: boolean;
    [key: string]: any;
  };
  error?: string;
}

// Login with username/password
export async function login(username: string, password: string): Promise<LoginResult> {
  try {
    const config = getCurrentParseConfig();
    console.log('🔐 Attempting login for:', username);
    
    const response = await fetch(`${config.serverURL}/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Parse-Application-Id': config.appId,
        // Note: X-Parse-Revocable-Session removed - requires CORS update on backend
      },
      body: JSON.stringify({ username, password }),
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      console.log('❌ Login failed:', data);
      return {
        success: false,
        error: data.error || 'Login failed',
      };
    }
    
    console.log('✅ Login successful:', data);
    return {
      success: true,
      user: data,
    };
  } catch (error) {
    console.error('💥 Login error:', error);
    return {
      success: false,
      error: (error as Error).message,
    };
  }
}

// Login with email/password (if supported)
export async function loginWithEmail(email: string, password: string): Promise<LoginResult> {
  try {
    const config = getCurrentParseConfig();
    // First try to find user by email, then login with username
    const response = await fetch(`${config.serverURL}/login?${new URLSearchParams({
      username: email,
      password: password,
    })}`, {
      method: 'GET',
      headers: {
        'X-Parse-Application-Id': config.appId,
        'X-Parse-Revocable-Session': '1',
      },
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      return {
        success: false,
        error: data.error || 'Login failed',
      };
    }
    
    return {
      success: true,
      user: data,
    };
  } catch (error) {
    return {
      success: false,
      error: (error as Error).message,
    };
  }
}

// Logout
export async function logout(sessionToken: string): Promise<boolean> {
  try {
    const config = getCurrentParseConfig();
    const response = await fetch(`${config.serverURL}/logout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Parse-Application-Id': config.appId,
        'X-Parse-Session-Token': sessionToken,
      },
    });
    
    return response.ok;
  } catch (error) {
    console.error('Logout error:', error);
    return false;
  }
}

// Validate session token
export async function validateSession(sessionToken: string): Promise<LoginResult> {
  try {
    const config = getCurrentParseConfig();
    const response = await fetch(`${config.serverURL}/users/me`, {
      headers: {
        'X-Parse-Application-Id': config.appId,
        'X-Parse-Session-Token': sessionToken,
      },
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      return {
        success: false,
        error: data.error || 'Invalid session',
      };
    }
    
    return {
      success: true,
      user: { ...data, sessionToken },
    };
  } catch (error) {
    return {
      success: false,
      error: (error as Error).message,
    };
  }
}

// Get current user (requires session token)
export async function getCurrentUser(sessionToken: string): Promise<any> {
  try {
    const config = getCurrentParseConfig();
    const response = await fetch(`${config.serverURL}/users/me`, {
      headers: {
        'X-Parse-Application-Id': config.appId,
        'X-Parse-Session-Token': sessionToken,
      },
    });
    
    if (!response.ok) {
      return null;
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error getting current user:', error);
    return null;
  }
}

// Request password reset
export async function requestPasswordReset(email: string): Promise<{ success: boolean; error?: string }> {
  try {
    const config = getCurrentParseConfig();
    const response = await fetch(`${config.serverURL}/requestPasswordReset`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Parse-Application-Id': config.appId,
      },
      body: JSON.stringify({ email }),
    });
    
    if (!response.ok) {
      const data = await response.json();
      return { success: false, error: data.error };
    }
    
    return { success: true };
  } catch (error) {
    return { success: false, error: (error as Error).message };
  }
}
